import { useEffect, useState, useRef } from "react";
import { SupportTicketMessage } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";

type UseVisibleSupportMessagesProps = {
    messages: SupportTicketMessage[];
    messagesListRef: React.RefObject<HTMLUListElement>;
    ticketId: string | undefined;
};

export function useVisibleSupportMessages({
    messages,
    messagesListRef,
    ticketId,
}: UseVisibleSupportMessagesProps) {
    // Храним только ID последнего просмотренного сообщения поддержки
    const [lastSeenSupportMessageId, setLastSeenSupportMessageId] = useState<number>(0);
    const lastSeenSupportMessageIdRef = useRef<number>(0);
    const ticketIdRef = useRef<string | undefined>(ticketId);

    // Сбрасываем состояние при изменении ticketId
    useEffect(() => {
        if (ticketIdRef.current !== ticketId) {
            ticketIdRef.current = ticketId;
            lastSeenSupportMessageIdRef.current = 0;
            setLastSeenSupportMessageId(0);
        }
    }, [ticketId]);

    useEffect(() => {
        const list = messagesListRef.current;
        if (!list || !messages.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                let maxVisibleId = lastSeenSupportMessageIdRef.current;

                entries.forEach((entry) => {
                    const target = entry.target as HTMLElement;
                    const idAttr = target.getAttribute("data-message-id");
                    const bySupport = target.getAttribute("data-message-by-support");

                    if (bySupport !== "Y" || !idAttr) return;

                    const id = Number(idAttr);
                    if (!Number.isFinite(id)) return;

                    if (entry.isIntersecting && id > maxVisibleId) {
                        maxVisibleId = id;
                    }
                });

                // Обновляем только если нашли сообщение с большим ID
                if (maxVisibleId > lastSeenSupportMessageIdRef.current) {
                    lastSeenSupportMessageIdRef.current = maxVisibleId;
                    setLastSeenSupportMessageId(maxVisibleId);
                }
            },
            {
                root: list,
                threshold: 0.6,
            },
        );

        const items = list.querySelectorAll<HTMLElement>("[data-message-id]");
        items.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [messages, messagesListRef]);

    // Функция для программной пометки всех сообщений поддержки выше текущей позиции скролла как просмотренных
    const markMessagesAboveAsSeen = () => {
        const list = messagesListRef.current;
        if (!list || !messages.length) return;

        const listRect = list.getBoundingClientRect();
        const listBottom = listRect.top + list.clientHeight;

        let maxIdAboveViewport = lastSeenSupportMessageIdRef.current;

        // Находим все элементы сообщений поддержки
        const allMessageElements = list.querySelectorAll<HTMLElement>("[data-message-id]");
        
        allMessageElements.forEach((element) => {
            const bySupport = element.getAttribute("data-message-by-support");
            if (bySupport !== "Y") return;

            const idAttr = element.getAttribute("data-message-id");
            if (!idAttr) return;

            const id = Number(idAttr);
            if (!Number.isFinite(id)) return;

            // Используем getBoundingClientRect для определения позиции элемента
            const elementRect = element.getBoundingClientRect();
            const elementBottom = elementRect.bottom;

            // Проверяем, находится ли элемент выше или в текущей видимой области
            // (элемент считается просмотренным, если его нижняя граница выше или равна нижней границе видимой области)
            if (elementBottom <= listBottom && id > maxIdAboveViewport) {
                maxIdAboveViewport = id;
            }
        });

        // Обновляем только если нашли сообщение с большим ID
        if (maxIdAboveViewport > lastSeenSupportMessageIdRef.current) {
            lastSeenSupportMessageIdRef.current = maxIdAboveViewport;
            setLastSeenSupportMessageId(maxIdAboveViewport);
        }
    };

    return { lastSeenSupportMessageId, markMessagesAboveAsSeen };
}

